import React from 'react'
import type { SeatData, SeatDisplayStatus, VenueSectionData, SelectedSeat } from './SeatMap.types'

// Layout tipo "plano de teatro": separa cada fila en 3 bloques (izquierda / centro /
// derecha) con pasillos, indentando las filas más cortas (F1/F2) como en el plano real,
// muestra el ESCENARIO abajo (F1 es la fila más cercana) y el título del recinto.
// Pensado para venues de una sola sección con numeración continua por fila
// (ej. Teatro Casa de la Cultura: bloque central de 19 butacas).

const SEAT = 22
const GAP = 4
const AISLE = 26
const CENTER = 19 // butacas del bloque central por fila

const COLORS: Record<SeatDisplayStatus, string> = {
  AVAILABLE: '#22c55e',
  SOLD: '#ef4444',
  RESERVED_TEMP: '#f59e0b',
  SELECTED: '#eab308',
  BLOCKED_ADMIN: '#374151',
  OFFLINE: '#4b5563',
}

const CURSORS: Record<SeatDisplayStatus, string> = {
  AVAILABLE: 'pointer',
  SELECTED: 'pointer',
  SOLD: 'not-allowed',
  RESERVED_TEMP: 'not-allowed',
  BLOCKED_ADMIN: 'not-allowed',
  OFFLINE: 'default',
}

function Seat({ seat, status, onClick }: { seat: SeatData; status: SeatDisplayStatus; onClick: () => void }) {
  const interactive = status === 'AVAILABLE' || status === 'SELECTED'
  return (
    <button
      type="button"
      disabled={!interactive}
      onClick={interactive ? onClick : undefined}
      title={seat.label}
      aria-label={`Asiento ${seat.label} — ${status}`}
      style={{
        width: SEAT,
        height: SEAT,
        borderRadius: 4,
        border: 'none',
        padding: 0,
        background: COLORS[status],
        color: status === 'AVAILABLE' ? '#14532d' : 'white',
        fontSize: 8,
        fontWeight: 600,
        lineHeight: `${SEAT}px`,
        cursor: CURSORS[status],
        opacity: status === 'OFFLINE' ? 0.35 : 1,
        flex: '0 0 auto',
      }}
    >
      {seat.seatNumber}
    </button>
  )
}

const blockWidth = (count: number) => (count > 0 ? count * SEAT + (count - 1) * GAP : 0)

export interface TeatroLayoutProps {
  sections: VenueSectionData[]
  selectedSeats: SelectedSeat[]
  onSeatClick: (seat: SeatData) => void
  onConflictRef?: (flashFn: (seatId: number) => void) => void
  venueName?: string | null
}

export function TeatroLayout({ sections, selectedSeats, onSeatClick, onConflictRef, venueName }: TeatroLayoutProps) {
  // Registrar un flash no-op para mantener la interfaz de los otros layouts.
  React.useEffect(() => {
    onConflictRef?.(() => {})
  }, [onConflictRef])

  const selectedIds = new Set(selectedSeats.map((s) => s.seatId))
  const statusOf = (seat: SeatData): SeatDisplayStatus =>
    selectedIds.has(seat.id) ? 'SELECTED' : seat.status

  function renderSection(section: VenueSectionData) {
    // Ancho fijo del bloque lateral (según la fila más ancha) para que las filas
    // cortas queden indentadas hacia el pasillo, igual que el plano.
    const maxSide = Math.max(0, ...section.rows.map((r) => Math.floor((r.seats.length - CENTER) / 2)))
    const sideW = blockWidth(maxSide)
    const centerW = blockWidth(CENTER)

    // Filas de la más lejana (F8, arriba) a la más cercana al escenario (F1, abajo).
    const rows = [...section.rows].sort((a, b) =>
      b.rowLabel.localeCompare(a.rowLabel, undefined, { numeric: true }),
    )

    const seatBtns = (arr: SeatData[]) =>
      arr.map((s) => <Seat key={s.id} seat={s} status={statusOf(s)} onClick={() => onSeatClick(s)} />)

    return (
      <div key={section.id} className="flex flex-col gap-1">
        {rows.map((row) => {
          const n = row.seats.length
          const side = Math.max(0, Math.floor((n - CENTER) / 2))
          const left = row.seats.slice(0, side)
          const center = row.seats.slice(side, side + CENTER)
          const right = row.seats.slice(side + CENTER)
          const label = (
            <span className="w-5 shrink-0 text-center text-[11px] text-white/70">{row.rowLabel}</span>
          )
          return (
            <div key={row.rowLabel} className="flex items-center gap-1">
              {label}
              <div style={{ width: sideW, display: 'flex', gap: GAP, justifyContent: 'flex-end' }}>
                {seatBtns(left)}
              </div>
              <div style={{ width: AISLE, flex: '0 0 auto' }} />
              <div style={{ width: centerW, display: 'flex', gap: GAP, justifyContent: 'center' }}>
                {seatBtns(center)}
              </div>
              <div style={{ width: AISLE, flex: '0 0 auto' }} />
              <div style={{ width: sideW, display: 'flex', gap: GAP, justifyContent: 'flex-start' }}>
                {seatBtns(right)}
              </div>
              {label}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-3 w-max mx-auto" data-testid="teatro-layout">
      {venueName && (
        <div className="text-center text-base font-bold uppercase tracking-wide text-secondary-120">
          {venueName}
        </div>
      )}

      <div className="w-fit">{sections.map(renderSection)}</div>

      {/* Escenario abajo — F1 es la fila más cercana */}
      <div
        className="mt-1 rounded-md px-12 py-1.5 text-xs font-bold uppercase"
        style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.3em' }}
      >
        ESCENARIO
      </div>
    </div>
  )
}
